/* RowDrawer.tsx — the receipts panel, and the per-value lineage trace behind it.
 *
 * Provenance at the grain of a single value: how one cell became the next, and what else that
 * cell moved.
 * The Engine room answers that for the DATASET. This answers it for ONE VALUE in ONE
 * ROW — upstream to the file it came from, downstream to everything it moves.
 *
 * Two things it must never do, both learned:
 * · It reads /api/row, not the grid row in hand. The drawer needs EVERY field, not the dozen the
 *   grid happens to be showing; reading it off the grid row is how a provenance panel ends up full
 *   of em-dashes.
 * · A step that cannot say something TRUE about this particular row is DROPPED by the server, not
 *   padded. So two rows in the same column legitimately return different chains, and the drawer
 *   renders what came back rather than a fixed template.
 */
import { useEffect, useState } from 'react';
import { api, isErr, type Entity, type Meta } from '../lib/api.ts';
import { N, localTime, safeUrl } from '../lib/fmt.ts';
import { Btn, Chip, ErrBox, Hint, Section, HAIR } from './ui.tsx';

interface Step {
  id: string; depth: number; kind: 'root' | 'op' | 'field'; rootKind?: string;
  label: string; line: string | null; val?: unknown; warn?: unknown;
  entity?: Entity; sameRow?: boolean; ops?: string[];
}
interface CellTrace {
  entity: Entity; key: string; col: string; label: string; group: string; type: string;
  value: unknown; src: string | null; subject: string;
  self: Step; up: Step[]; down: Step[];
  ops: { id: string; cls: string; what: string; script: string; n: number | null; plain: string; risk: string; reversible: string }[];
  surfaces: string[]; traced: boolean;
}

type Row = Record<string, unknown> & {
  _tags?: string[]; _man?: string[];
  _acts?: { kind: string; note: string | null; by: string; at: string }[];
};

export function RowDrawer({ meta, entity, id, onClose }: {
  meta: Meta; entity: Entity; id: string | number; onClose: () => void;
}) {
  const [row, setRow] = useState<Row | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [col, setCol] = useState<string | null>(null);
  const [group, setGroup] = useState<string | null>(null);

  useEffect(() => {
    let dead = false;
    setRow(null); setErr(null); setCol(null);
    api<Row>(`row?e=${entity}&key=${encodeURIComponent(String(id))}`).then((r) => {
      if (dead) return;
      if (isErr(r)) setErr(r.error); else setRow(r);
    });
    return () => { dead = true; };
  }, [entity, id]);

  const fields = meta.fields[entity];
  const groups = [...new Set(fields.map((f) => f.grp || 'Other'))];
  const shownGroups = group ? groups.filter((g) => g === group) : groups;

  const title = row
    ? String(row.full_name ?? row.name ?? row.site ?? `${entity} ${id}`)
    : `${entity} ${id}`;

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 40, display: 'flex', justifyContent: 'flex-end',
      background: 'rgba(3,4,6,.55)',
    }} onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        style={{ width: 'min(980px, 92vw)', background: 'var(--s0)', borderLeft: HAIR, display: 'flex', flexDirection: 'column', minHeight: 0 }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '0 var(--gut)', height: 44, borderBottom: HAIR, flex: '0 0 auto' }}>
          <span className="t-label">{entity === 'ppl' ? 'Contact' : entity === 'acct' ? 'Account' : 'Plant'}</span>
          <span style={{ fontSize: 'var(--t-lead)', fontWeight: 600 }}>{title}</span>
          <div style={{ marginLeft: 'auto' }}><Btn sm onClick={onClose}>close ✕</Btn></div>
        </div>

        {err && <ErrBox what="That row did not load" msg={err} />}
        {!row && !err && <Hint style={{ padding: 'var(--gut)' }}>loading the record…</Hint>}

        {row && (
          <div style={{ flex: '1 1 auto', display: 'flex', minHeight: 0 }}>
            {/* every field, grouped, with its provenance at the point of use */}
            <div style={{ flex: '1 1 auto', overflowY: 'auto', minWidth: 0 }}>
              {(row._man?.length || row._tags?.length) && (
                <Section label="Tags">
                  <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                    {row._tags?.map((t) => <Chip key={t} tone="info" title="derived — computed from this row's own columns">{t}</Chip>)}
                    {row._man?.map((t) => <Chip key={t} tone="warn" title="set by hand in this app">{t}</Chip>)}
                  </div>
                </Section>
              )}
              {row._acts && row._acts.length > 0 && (
                <Section label="Activity">
                  {row._acts.map((a, i) => (
                    <div key={i} style={{ display: 'flex', gap: 8, padding: '3px 0', borderBottom: HAIR }}>
                      <Chip>{a.kind}</Chip>
                      <span style={{ flex: '1 1 auto', color: 'var(--dim)' }}>{a.note || ''}</span>
                      <span className="t-micro" style={{ color: 'var(--dim2)' }}>{a.by} · {localTime(a.at)}</span>
                    </div>
                  ))}
                </Section>
              )}

              <div style={{ display: 'flex', gap: 3, flexWrap: 'wrap', padding: 'var(--gut)', borderBottom: HAIR }}>
                <Btn sm on={!group} onClick={() => setGroup(null)}>All groups</Btn>
                {groups.map((g) => <Btn key={g} sm on={group === g} onClick={() => setGroup(g)}>{g}</Btn>)}
              </div>

              {shownGroups.map((g) => (
                <Section key={g} label={g}>
                  {fields.filter((f) => (f.grp || 'Other') === g).map((f) => {
                    const v = row[f.k];
                    const url = f.k.includes('url') || f.k.includes('linkedin') ? safeUrl(v) : '';
                    return (
                      <div key={f.k} onClick={() => setCol(f.k)} title={f.src}
                        style={{
                          display: 'flex', gap: 10, alignItems: 'baseline', padding: '4px 0',
                          borderBottom: HAIR, cursor: 'pointer',
                          background: col === f.k ? 'rgba(255,170,0,.043)' : undefined,
                        }}>
                        <span className="t-label" style={{ width: 190, flex: '0 0 auto' }}>{f.lbl}</span>
                        <span className={f.t === 'num' ? 'num' : undefined} style={{ flex: '1 1 auto', minWidth: 0, color: v == null || v === '' ? 'var(--dim2)' : 'var(--ink)', wordBreak: 'break-word' }}>
                          {v == null || v === '' ? '—'
                            : url ? <a href={url} target="_blank" rel="noopener" style={{ color: 'var(--bl)' }} onClick={(e) => e.stopPropagation()}>{String(v)}</a>
                              : f.t === 'num' ? N(v) : String(v)}
                        </span>
                        <span className="t-micro" style={{ color: 'var(--dim2)', width: 46, textAlign: 'right', flex: '0 0 auto' }}>trace →</span>
                      </div>
                    );
                  })}
                </Section>
              ))}
            </div>

            {/* the per-value chain */}
            <div style={{ width: 420, flex: '0 0 420px', borderLeft: HAIR, overflowY: 'auto' }}>
              {col
                ? <CellPanel entity={entity} id={id} col={col} />
                : <Hint style={{ padding: 'var(--gut)' }}>
                  Click any value on the left for its chain — upstream to the file it came from, downstream to
                  everything it moves. Both directions are read off the same edge list, so they cannot disagree.
                </Hint>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CellPanel({ entity, id, col }: { entity: Entity; id: string | number; col: string }) {
  const [t, setT] = useState<CellTrace | null>(null);
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    let dead = false;
    setT(null); setErr(null);
    api<CellTrace>(`cell?e=${entity}&key=${encodeURIComponent(String(id))}&col=${encodeURIComponent(col)}`).then((r) => {
      if (dead) return;
      if (isErr(r)) setErr(r.error); else setT(r);
    });
    return () => { dead = true; };
  }, [entity, id, col]);

  if (err) return <ErrBox what="That value could not be traced" msg={err} />;
  if (!t) return <Hint style={{ padding: 'var(--gut)' }}>tracing…</Hint>;

  return (
    <div>
      <Section label={t.label} right={<Chip tone={t.traced ? 'info' : 'soft'}>{t.traced ? 'traced' : 'no edges recorded'}</Chip>}>
        <div style={{ fontSize: 'var(--t-fig)', color: 'var(--ink)', wordBreak: 'break-word' }}>
          {t.value == null || t.value === '' ? '—' : String(t.value)}
        </div>
        {t.src && <Hint style={{ marginTop: 6 }}>{t.src}</Hint>}
      </Section>

      {t.up.length > 0 && (
        <Section label="Upstream — where this came from">
          {t.up.map((s) => <StepRow key={s.id} s={s} />)}
        </Section>
      )}
      {t.down.length > 0 && (
        <Section label="Downstream — what it moves">
          {t.down.map((s) => <StepRow key={s.id} s={s} />)}
        </Section>
      )}
      {t.ops.length > 0 && (
        <Section label="Operations named in this chain">
          {t.ops.map((o) => (
            <div key={o.id} style={{ padding: '5px 0', borderBottom: HAIR }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
                <Chip>{o.cls}</Chip>
                <span style={{ flex: '1 1 auto' }}>{o.what}</span>
                <span className="num t-micro" style={{ color: 'var(--am)' }}>{o.n != null ? N(o.n) : ''}</span>
              </div>
              <Hint style={{ marginTop: 2 }}>{o.plain}</Hint>
              <Hint style={{ color: 'var(--dim2)', marginTop: 2 }}>
                <span style={{ fontFamily: 'var(--mono)' }}>{o.script}</span> · risk: {o.risk} · reversible: {o.reversible}
              </Hint>
            </div>
          ))}
        </Section>
      )}
      {t.surfaces.length > 0 && (
        <Section label="Where this value is used">
          <Hint>{t.surfaces.join(' · ')}</Hint>
        </Section>
      )}
    </div>
  );
}

function StepRow({ s }: { s: Step }) {
  return (
    <div style={{ paddingLeft: Math.min(4, s.depth) * 12, padding: '4px 0', borderBottom: HAIR }}>
      <div style={{ display: 'flex', gap: 6, alignItems: 'baseline' }}>
        {s.kind === 'root' && <Chip tone="info">{s.rootKind || 'source'}</Chip>}
        <span style={{ fontWeight: 600, color: s.sameRow === false ? 'var(--dim)' : 'var(--ink)' }}>{s.label}</span>
        {s.val != null && s.val !== '' && (
          <span className="num t-micro" style={{ marginLeft: 'auto', color: 'var(--am)' }}>{String(s.val)}</span>
        )}
      </div>
      {s.line && <Hint style={{ marginTop: 2 }}>{s.line}</Hint>}
      {/* A warning attached to THIS row, not to the column in general. That is the whole reason two
          rows in the same column return different chains. */}
      {s.warn != null && s.warn !== '' && (
        <Hint style={{ marginTop: 2, color: 'var(--rd)' }}>{String(s.warn)}</Hint>
      )}
    </div>
  );
}
